import SentryFullStory from "@sentry/fullstory";
import * as Sentry from "@sentry/react";
import { Breadcrumb, BreadcrumbHint } from "@sentry/react";
import { filter, pick, trim } from "lodash";
import { Seller, User, UserRole } from "types";
import { config } from "../config";
import { isXolaAdmin } from "../models/User";
import { getReleaseInfo } from "./helpers";
import { registerSnippet } from "./index";
import { findValue } from "@/api/interceptors";

const SNIPPET_NAME = "Sentry";

registerSnippet(SNIPPET_NAME, !!config.sentry.dsn, (user: User, seller: Seller) => {
    const { branch, release, isPreview, isLocalhost, env: environment, isProdLike } = getReleaseInfo();

    const style = "color:black;font-size:10px;";
    if (user?.roles.includes(UserRole.ADMIN)) {
        console.log(`%cAdmin: ENV: ${environment} Branch: ${branch} Release: ${release} Preview: ${isPreview}`, style);
    }

    if (!seller || !user) {
        return;
    }

    if (isProdLike && !isLocalhost) {
        const higherTraceRateURLs = [/purchases\/create\/[a-z\d]+/i, /dashboard\/\d{4}-\d{2}-\d{2}/i];
        const excludeBreadcrumbs = [/you may test your stripe\.js integration over http/i, /\[vite] hot updated/i];
        const excludeMessages = [
            /resizeobserver loop limit exceeded/i,
            /resizeobserver loop completed/i,
            /request failed with status code (400|401|402|403|404)/i,
            /network error/i,
            /request has been terminated/i,
        ];
        Sentry.setTag("is_preview", isPreview); // Tag an amplify preview so we can use it for filtering

        const integrations: any = [];
        if (config.fullstory.id && config.sentry.organization) {
            integrations.push(new SentryFullStory(config.sentry.organization));
        }

        // https://docs.sentry.io/platforms/javascript/guides/react/
        Sentry.init({
            dsn: config.sentry.dsn,
            release,
            // debug: true,
            environment,
            maxBreadcrumbs: 50, // Default is 100 which may be too much
            attachStacktrace: true, // When enabled, stack traces are automatically attached to all messages logged
            // The BrowserTracing integration creates a new transaction for each page load and navigation event, and
            // creates a child span for every XHR request that occurs while those transactions are open
            integrations,
            tracesSampler(samplingContext) {
                const { location } = samplingContext;
                for (const re of higherTraceRateURLs) {
                    if (location && re.test(location?.pathname)) {
                        return 0.05; // 5% of these transactions should be traced
                    }
                }

                return 0.02; // Default only sample 2% of all transactions
            },
            beforeSend(event: any, hint: any) {
                // Ignore errors that occur while the user is offline
                if (!navigator.onLine) {
                    return null;
                }

                const message = event.message ?? hint.originalException?.message ?? hint.originalException ?? "";
                console.debug("Sentry data check", { message, event, hint });
                for (const matcher of excludeMessages) {
                    if (matcher.test(message)) {
                        console.debug(`%cSentry: Excluding message: ${message}`, style);
                        return null;
                    }
                }

                const browserShort = /\/(\w+)\//.exec(window.location.pathname)?.[0];
                const path = trim(browserShort, "/");

                try {
                    let reason = null;
                    if (event.contexts?.api_response?.response?.includes("reason")) {
                        reason = findValue(JSON.parse(event.contexts?.api_response?.response), "reason");
                        if (reason) {
                            event.fingerprint = ["{{ default }}", reason];
                            console.debug("   Created fingerprint", event.fingerprint);
                        }
                    }

                    if (event.fingerprint && Array.isArray(event.fingerprint)) {
                        event.fingerprint.push(path);
                    }

                    const exceptionType = event.exception?.values?.[0]?.type;
                    console.debug("Exception Type", exceptionType);
                    if (exceptionType === "Error") {
                        const code = event.tags?.http_code ?? "Error";
                        const exceptionReason = reason ? `HTTP ${code}: ${reason}` : message.replace("Error: ", "");
                        event.exception.values[0].type = exceptionReason?.trim();
                        console.debug("   New exception type is:", event.exception.values[0].type);
                    }

                    if (exceptionType === "TypeError") {
                        const exceptionReason = `${path ? path + " - " : ""} ${hint.originalException.message?.trim()}`;
                        event.exception.values[0].type = exceptionReason.trim();
                        console.debug("   New exception type is:", event.exception.values[0].type);
                    }
                } catch (error) {
                    console.debug("Sentry finger print error", { error, message, event });
                }

                return event;
            },
            beforeBreadcrumb(breadcrumb: Breadcrumb, hint?: BreadcrumbHint) {
                for (const matcher of excludeBreadcrumbs) {
                    if (breadcrumb.message && matcher.test(breadcrumb.message)) {
                        return null;
                    }
                }

                if (breadcrumb && hint?.event.srcElement?.hasAttributes()) {
                    const source = hint.event.srcElement;
                    try {
                        const skipAttributes = new Set(["class", "viewBox", "xmlns"]);
                        const attributes = filter(source.attributes, (a: any) => !skipAttributes.has(a.name));
                        const output = attributes.map((attribute: any) => `${attribute.name}: ${attribute.value}`);
                        const prefix = output.length > 0 ? `🔺Attributes – ${output.join(" ")} 💡🔺 Classes: ` : "";
                        breadcrumb.message = `${prefix}${breadcrumb.message}`;
                    } catch (error) {
                        console.error(error);
                    }
                }

                return breadcrumb;
            },
        });

        const payload: any = {
            id: user.id,
            name: user.name || "Unknown",
            email: user.email,
            ip_address: "{{auto}}",
            user: { ...pick(user, ["id", "name", "email", "type"]) },
            seller: pick(seller, ["id", "email", "name", "type", "timezoneName", "version", "apiVersion"]),
        };
        // payload.seller.roles = seller.roles.join(","); // Convert roles to string otherwise it is a massive array in Sentry
        Sentry.setUser(payload);

        // These are set so that we can use Sentry's search functionality to find the user & seller
        // Also for convenience since it will also show up at the top of the dashboard and allow us a quick glance
        Sentry.setTag("user_name", user.name);
        Sentry.setTag("is_xola_admin", isXolaAdmin(user));
        if (seller) {
            Sentry.setTag("seller_id", seller.id);
            Sentry.setTag("seller_name", seller.name);
            Sentry.setTag("seller_email", seller.email);
        }
    } else if (isLocalhost) {
        console.log("%cYou have Sentry enabled in localhost. This is not recommended.", "color: red");
        console.log("%cPlease update config/local.json and remove the 'sentry' config", "color: red");
    }
});
